// src/utils/buildExtractionTabs.tsx
import type { TabsProps } from 'antd';
import { Typography } from 'antd';

const { Text } = Typography;

export type ExtractEntry = {
  value?: unknown;
  source_text?: string | string[];
  location?: unknown | unknown[];
  [k: string]: unknown;
};

export type ExtractRecord<TEntry extends ExtractEntry = ExtractEntry> = Record<
  string,
  TEntry
>;

export interface BuildExtractionTabsOptions<TEntry extends ExtractEntry> {
  tabKey?: string;
  tabLabel?: string;
  formatKey?: (key: string) => string;
  valueSelector?: (entry: TEntry) => string | React.ReactNode;
  orderedKeys?: string[];
  filterKey?: (key: string, entry: TEntry) => boolean;
  copyable?: boolean;
}

export function buildExtractionTabs<TEntry extends ExtractEntry>(
  data: ExtractRecord<TEntry>,
  opts: BuildExtractionTabsOptions<TEntry> = {},
): TabsProps['items'] {
  const {
    tabKey = '1',
    tabLabel = 'Extracted Fields',
    formatKey = (key: string) =>
      key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    valueSelector = (entry) => {
      if (!Array.isArray(entry?.value)) {
        return String(entry?.value ?? '');
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entry.value.map((v: any, idx: number) => {
            if (typeof v === 'string' || typeof v === 'number') {
              return <div key={idx}>{String(v)}</div>;
            }
            return (
              <div
                key={idx}
                style={{
                  border: '1px solid #eee',
                  padding: 6,
                  borderRadius: 4,
                }}
              >
                {Object.entries(v)
                  .filter(([k]) => k !== 'location')
                  .map(([k, val]) => (
                    <div key={k}>
                      <strong>{formatKey(k)}:</strong> {String(val)}
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      );
    },
    orderedKeys,
    filterKey,
    copyable = true,
  } = opts;

  const keys = (orderedKeys ?? Object.keys(data)).filter((k) =>
    filterKey ? filterKey(k, data[k]) : true,
  );

  const renderSourceText = (src: string | string[]) => {
    if (!src) return null;

    if (Array.isArray(src)) {
      return (
        <ul style={{ margin: '4px 0 0 0', paddingLeft: 16, color: '#666' }}>
          {src.map((s, i) => (
            <li key={i} style={{ fontSize: 12 }}>
              {s}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{src}</div>
    );
  };

  return [
    {
      key: tabKey,
      label: tabLabel,
      children: keys.map((key) => {
        const entry = data[key];
        const display = valueSelector(entry);

        return (
          <div key={key} style={{ marginBottom: 12 }}>
            <strong>{formatKey(key)}:</strong>
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong>Value:</strong>
                {typeof display === 'string' ? (
                  copyable ? (
                    <Text
                      copyable={{ text: display }}
                      style={{ marginLeft: 4 }}
                    >
                      {display}
                    </Text>
                  ) : (
                    <Text style={{ marginLeft: 4 }}>{display}</Text>
                  )
                ) : (
                  display
                )}
              </div>

              {entry?.source_text ? (
                <div style={{ marginTop: 6 }}>
                  <strong style={{ fontSize: 12, color: '#888' }}>
                    Source:
                  </strong>
                  {renderSourceText(entry.source_text)}
                </div>
              ) : null}
            </div>
          </div>
        );
      }),
    },
  ];
}
